<template>
  <header class="feed-header">

    <div class="feed-header-inner">

      <!-- =====================================================
           LOGO
      ====================================================== -->

      <div class="header-logo-area">
        <img
          src="../assets/hr3logo.png"
          alt="Логотип"
          class="header-logo"
          @click="navigate('/feed')"
        />
      </div>


      <!-- =====================================================
           NAVIGATION
      ====================================================== -->

      <nav class="nav-buttons">

        <button
          v-for="(tab, index) in tabs"
          :key="index"
          :class="{ active: activeTab === tab.route }"
          @click="navigate(tab.route)"
        >
          <span class="tab-label">
            {{ tab.label }}
          </span>

          <span
            v-if="
              tab.route === '/dialogs' &&
              hasNewMessages
            "
            class="dialogs-dot"
          ></span>

          <span
            v-if="tab.badge"
            class="tab-badge"
          >
            {{ tab.badge }}
          </span>
        </button>

      </nav>


      <!-- =====================================================
           RIGHT SIDE
      ====================================================== -->

      <div class="avatar-wrapper">

        <div class="action-buttons">

          <button
            class="icon-button"
            @click="goToNotifications"
          >
            <img
              src="../assets/bell-icon.png"
              alt="Уведомления"
            />

            <span
              v-if="settings?.isNewResponses"
              class="notification-dot"
            ></span>
          </button>


          <button
            class="icon-button"
            @click="goToFavorites"
          >
            <img
              src="../assets/heart-icon.png"
              alt="Избранное"
            />
          </button>

        </div>


        <!-- AVATAR SKELETON -->

        <template v-if="isAvatarLoading">
          <div class="avatar-skeleton shimmer"></div>
        </template>


        <!-- AVATAR -->

        <template v-else>

          <div
            class="avatar-progress-wrapper"
            @click="toggleSettings"
          >

            <div class="profile-tooltip">
              Ваш профиль заполнен на
              <strong>{{ profileCompletion }}%</strong>
            </div>


            <svg
              class="avatar-progress-ring"
              viewBox="0 0 52 52"
            >
              <defs>
                <linearGradient
                  id="avatarGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stop-color="#2887F7"
                  />

                  <stop
                    offset="50%"
                    stop-color="#7C5CFF"
                  />

                  <stop
                    offset="100%"
                    stop-color="#00D4FF"
                  />
                </linearGradient>
              </defs>


              <circle
                class="progress-bg"
                cx="26"
                cy="26"
                r="23"
              />

              <circle
                class="progress-bar"
                cx="26"
                cy="26"
                r="23"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="progressOffset"
              />

            </svg>


            <img
              :src="avatarUrl"
              alt="Аватар"
              class="profile-avatar"
            />

          </div>

        </template>


        <NotificationsDropdown
          v-if="isNotificationsVisible"
        />


        <!-- =====================================================
             SETTINGS DROPDOWN
        ====================================================== -->

        <div
          v-if="isDropdownVisible"
          class="settings-dropdown"
        >

          <div class="section">

            <div class="section-title">
              ОСНОВНОЕ
            </div>


            <div
              class="item"
              @click="goToProfile"
            >
              <img
                :src="getIcon('profile')"
                class="icon"
              />

              <span>
                Профиль
              </span>
            </div>


            <div
              class="item"
              @click="goToFavorites"
            >
              <img
                :src="getIcon('favorites')"
                class="icon"
              />

              <span>
                Избранное
              </span>

              <span
                v-if="
                  !isLoadingSettings &&
                  settings?.favoritesCount > 0
                "
                class="badge"
              >
                {{ settings.favoritesCount }}
              </span>

              <span
                v-else-if="isLoadingSettings"
                class="badge-skeleton"
              ></span>
            </div>


            <div
              ref="myVacanciesItemRef"
              class="item"
              :class="{
                disabled: !settings?.isEmployer
              }"
              @click="handleMyVacanciesClick"
            >
              <img
                :src="getIcon('vacancies')"
                class="icon"
              />

              <span>
                Мои вакансии
              </span>

              <span
                v-if="!settings?.isEmployer"
                class="disabled-hint"
              >
                Для рекрутеров
              </span>

              <span
                v-if="
                  !isLoadingSettings &&
                  settings?.myVacanciesCount > 0
                "
                class="badge"
              >
                {{ settings.myVacanciesCount }}
              </span>

              <span
                v-else-if="isLoadingSettings"
                class="badge-skeleton"
              ></span>
            </div>


            <div
              class="item"
              :class="{
                disabled: !settings?.isEmployer
              }"
              @click="handleMyEventsClick"
            >
              <img
                :src="getIcon('events')"
                class="icon"
              />

              <span>
                Мои ивенты
              </span>

              <span
                v-if="!settings?.isEmployer"
                class="disabled-hint"
              >
                Для рекрутеров
              </span>
            </div>


            <div
              class="item"
              @click="goToSubscribes"
            >
              <img
                :src="getIcon('companies')"
                class="icon"
              />

              <span>
                Подписки на компании
              </span>
            </div>


            <div
              class="item"
              @click="goToAllCompanies"
            >
              <img
                :src="getIcon('search')"
                class="icon"
              />

              <span>
                Поиск компаний
              </span>
            </div>


            <div
              class="item"
              @click="goToFriends"
            >
              <img
                :src="getIcon('friends')"
                class="icon"
              />

              <span>
                Френдс+Реквестс
              </span>

              <span
                v-if="
                  !isLoadingSettings &&
                  settings?.friendsCount > 0
                "
                class="badge"
              >
                {{ settings.friendsCount }}
              </span>

              <span
                v-else-if="isLoadingSettings"
                class="badge-skeleton"
              ></span>
            </div>


            <div
              v-if="settings?.isAdmin"
              class="admin-item"
              @click="goToAdmin"
            >
              <img
                :src="adminIcon"
                class="icon"
              />

              <span>
                Админка
              </span>
            </div>

          </div>


          <div class="section">

            <div class="section-title">
              НАСТРОЙКИ
            </div>


            <div
              class="item"
              @click="goToSettings"
            >
              <img
                :src="getIcon('settings')"
                class="icon"
              />

              <span>
                Настройки
              </span>
            </div>


            <div
              class="item"
              @click="goToQuestions"
            >
              <img
                :src="getIcon('questions')"
                class="icon"
              />

              <span>
                Вопросы
              </span>
            </div>


            <div
              class="item"
              @click="logoutConfirm"
            >
              <img
                :src="getIcon('exit')"
                class="icon"
              />

              <span>
                Выйти
              </span>
            </div>

          </div>

        </div>

      </div>

    </div>


    <div class="header-separator"></div>

  </header>


  <ModalConfirm
    :isVisible="showLogoutModal"
    titleText="Выйти из аккаунта?"
    messageText="Вы точно хотите выйти из аккаунта?"
    cancelText="Отмена"
    confirmText="Выйти"
    @close="showLogoutModal = false"
    @confirm="logout"
  />


  <MyVacanciesTutorialOverlay
    :visible="showMyVacanciesTutorial"
    :target-element="myVacanciesItemRef"
    @close="handleMyVacanciesTutorialClose"
  />

</template>


<script setup>

import PendingCompanyLevelService from "../api/PendingCompanyLevelService";
import { authStateReady } from "../api/ChatsService";

import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  nextTick
} from "vue";

import {
  useRouter,
  useRoute
} from "vue-router";

import {
  SettingsService
} from "../api/SettingsService";

import {
  getAuth,
  signOut
} from "firebase/auth";

import UserService
  from "../api/UserService";

import adminIcon
  from "../assets/admin-panel-icon.png";

import NotificationsDropdown
  from "./NotificationsDropdown.vue";

import ModalConfirm
  from "../components/ModalConfirm.vue";

import MyVacanciesTutorialOverlay
  from "./MyVacanciesTutorialOverlay.vue";

import defaultAvatar
  from "../assets/default-avatar.png";


  const emit = defineEmits([
  "show-employer-tutorial",
  "employer-status",
  "premium-status",
  "pending-company-level",
  "renew-subscription-status"
]);

const router =
  useRouter();

const route =
  useRoute();


const showLogoutModal =
  ref(false);

const isNotificationsVisible =
  ref(false);

const settings =
  ref(null);

const hasNewMessages =
ref(false);

const isLoadingSettings =
  ref(false);

const isAvatarLoading =
  ref(true);

const avatarUrl =
  ref(defaultAvatar);

const isDropdownVisible =
  ref(false);

const myVacanciesItemRef =
  ref(null);

const showMyVacanciesTutorial =
  ref(false);


const theme =
  ref(
    document.documentElement
      .getAttribute("data-theme") ||
    "light"
  );


const radius =
  23;

const circumference =
  2 * Math.PI * radius;


const tabs = [
  {
    label: "Лента",
    route: "/feed"
  },
  {
    label: "Коннект",
    route: "/connect"
  },
  {
    label: "Диалоги",
    route: "/dialogs"
  },
  {
    label: "Ивенты",
    route: "/events"
  },
  {
    label: "Фриланс",
    route: "/freelance",
    badge: "Новое"
  }
];


const activeTab =
  ref(route.path);


const profileCompletion =
  computed(() => {

    const value =
      settings.value
        ?.totalFillingCount ?? 0;

    return (
      value === 0
        ? 10
        : value
    );

  });


const progressOffset =
  computed(() => {

    return (
      circumference -
      (
        profileCompletion.value /
        100
      ) *
      circumference
    );

  });


const observer =
  new MutationObserver(
    () => {

      theme.value =
        document.documentElement
          .getAttribute(
            "data-theme"
          ) ||
        "light";

    }
  );


watch(
  () => route.path,
  (newPath) => {

    activeTab.value =
      newPath;

  }
);


/* ============================================================
   PENDING COMPANY LEVEL
============================================================ */

const checkPendingCompanyLevel = async () => {
  try {
    const user = await authStateReady();

    if (!user?.uid) {
      console.warn(
        "⚠️ Не удалось проверить уровень компании: пользователь не авторизован"
      );
      return;
    }

    const response =
      await PendingCompanyLevelService.getPendingLevelForCompany(
        user.uid
      );

    if (!response.hasPendingCompany) {
      console.log(
        "🏢 Компаний с неназначенным level нет"
      );
      return;
    }

    const companyEmployee = response.companyEmployee;

    console.log(
      "🏢 Найдена компания с неназначенным level:",
      companyEmployee
    );

    emit(
      "pending-company-level",
      companyEmployee
    );

  } catch (error) {
    console.error(
      "❌ Ошибка проверки уровня компании:",
      error
    );
  }
};



/* ============================================================
   SETTINGS
============================================================ */

const loadSettings =
  async () => {

    if (
      isLoadingSettings.value
    ) {
      return;
    }


    isLoadingSettings.value =
      true;


    try {

      console.log(
        "🚀 Загружаем настройки FeedHeader"
      );


      const response =
        await SettingsService
          .fetchSettings();


      settings.value =
        response;

        try {

          const additionalSettings =
            await SettingsService
              .fetchAdditionalSettings();

          // Проверяем компанию, у которой level == null.
          // Не блокируем обработку дополнительных настроек.
          void checkPendingCompanyLevel();

          hasNewMessages.value =
            additionalSettings?.hasNewMessages === true;

          const isPremiumEnabled =
          additionalSettings?.isPremiumEnabled === true;

          localStorage.setItem(
            "isPremiumEnabled",
            String(isPremiumEnabled)
          );

          console.log(
            "💎 FeedHeader premium:",
            isPremiumEnabled
          );

          emit(
            "premium-status",
            isPremiumEnabled
          );

          // ============================================================
          // RENEW SUBSCRIPTION
          // ============================================================

          const isShowRenewSubscribtions =
            additionalSettings?.isShowRenewSubscribtions === true ||
            additionalSettings?.is_show_renew_subscribtions === true;

          console.log(
            "💎 Нужно показать продление подписки:",
            isShowRenewSubscribtions
          );

          emit(
            "renew-subscription-status",
            isShowRenewSubscribtions
          );

          console.log(
            "💬 Дополнительные настройки:",
            additionalSettings
          );

          console.log(
            "🟠 Есть новые сообщения:",
            hasNewMessages.value
          );

          } catch (error) {

          console.warn(
            "⚠️ Не удалось получить дополнительные настройки:",
            error
          );

        }

      
        emit(
        "employer-status",
        response?.isEmployer === true
      );


      const shouldShowEmployerTutorial =
        response?.isEmployer === true &&
        response?.isFirstTutorialWasSeen === false;


      if (
        shouldShowEmployerTutorial
      ) {

        emit(
          "show-employer-tutorial"
        );

      }

    } catch (error) {

      console.error(
        "❌ Ошибка загрузки настроек:",
        error?.response?.data ||
        error?.message ||
        error
      );

    } finally {

      isLoadingSettings.value =
        false;

    }

  };


/* ============================================================
   AVATAR
============================================================ */

const updateAvatar =
  () => {

    const directAvatar =
      localStorage.getItem(
        "current_user_avatar"
      );


    if (directAvatar) {

      avatarUrl.value =
        directAvatar;

      isAvatarLoading.value =
        false;

      return;

    }


    try {

      const profile =
        JSON.parse(
          localStorage.getItem(
            "user_profile"
          )
        );


      avatarUrl.value =
        profile?.image_url ||
        defaultAvatar;

    } catch (error) {

      console.warn(
        "⚠️ Не удалось распарсить user_profile:",
        error
      );


      avatarUrl.value =
        defaultAvatar;

    } finally {

      isAvatarLoading.value =
        false;

    }

  };


const handleStorageAvatarUpdate =
  (event) => {

    if (
      event.key ===
      "current_user_avatar"
    ) {

      updateAvatar();

    }

  };


/* ============================================================
   MOUNT
============================================================ */

onMounted(
  async () => {

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );


    await loadSettings();


    try {

      const profile =
        await UserService
          .getProfile();


      console.log(
        "👤 Полученный профиль:",
        profile
      );


      updateAvatar();

    } catch (error) {

      console.error(
        "❌ Ошибка при загрузке профиля:",
        error
      );


      const status =
        error?.response?.status;


      if (
        [401, 403, 404]
          .includes(status)
      ) {

        const auth =
          getAuth();


        try {

          await auth.signOut();

        } catch (signOutError) {

          console.warn(
            "⚠️ Ошибка Firebase signOut:",
            signOutError
          );

        }


        localStorage.clear();

        window.location.href =
          "/";

        return;

      }


      updateAvatar();

    }


    window.addEventListener(
      "storage",
      handleStorageAvatarUpdate
    );

  }
);


onBeforeUnmount(
  () => {

    observer.disconnect();

    window.removeEventListener(
      "storage",
      handleStorageAvatarUpdate
    );

  }
);


/* ============================================================
   NAVIGATION
============================================================ */

const navigate =
  (path) => {

    router.push(path);

  };


const goToProfile =
  () => {

    router.push(
      "/profile"
    );

  };


const goToAdmin =
  () => {

    router.push(
      "/admin-panel"
    );

  };


const goToSubscribes =
  () => {

    router.push(
      "/subscriptions"
    );

  };


const goToSettings =
  () => {

    const isMobile =
      window.innerWidth <= 430;


    router.push({

      path:
        isMobile
          ? "/m-profile"
          : "/profile",

      query: {
        section:
          "other"
      }

    });

  };


const goToNotifications =
  () => {

    isNotificationsVisible.value =
      !isNotificationsVisible.value;

    isDropdownVisible.value =
      false;

  };


const goToFavorites =
  () => {

    router.push(
      "/favorites"
    );

  };


const goToAllCompanies =
  () => {

    router.push({
      name:
        "AllCompanies"
    });

  };


const goToQuestions =
  () => {

    router.push(
      "/questions"
    );

  };


const goToFriends =
  () => {

    router.push(
      "/friends"
    );

  };


const goToMyVacancies =
  () => {

    router.push(
      "/my-vacancies"
    );

  };


const goToMyEvents =
  () => {

    router.push(
      "/my-events"
    );

  };


/* ============================================================
   ACTIONS
============================================================ */

const toggleSettings =
  async () => {

    const willOpen =
      !isDropdownVisible.value;


    isDropdownVisible.value =
      willOpen;

    isNotificationsVisible.value =
      false;


    if (!willOpen) {

      showMyVacanciesTutorial.value =
        false;

      return;

    }


    await nextTick();


    if (
      settings.value?.isEmployer &&
      !settings.value
        ?.isSecondTutorialWasSeen &&
      myVacanciesItemRef.value
    ) {

      console.log(
        "🎓 Показываем второй tutorial «Мои вакансии»"
      );


      showMyVacanciesTutorial.value =
        true;

    }

  };


const handleMyVacanciesClick =
  () => {

    if (
      !settings.value?.isEmployer
    ) {

      console.log(
        "🚫 Мои вакансии доступны только работодателям"
      );

      return;

    }


    goToMyVacancies();

  };


const handleMyEventsClick =
  () => {

    if (
      !settings.value?.isEmployer
    ) {

      console.log(
        "🚫 Мои ивенты доступны только рекрутерам"
      );

      return;

    }


    goToMyEvents();

  };


const handleMyVacanciesTutorialClose =
  () => {

    showMyVacanciesTutorial.value =
      false;

  };


/* ============================================================
   LOGOUT
============================================================ */

const logoutConfirm =
  () => {

    showLogoutModal.value =
      true;

  };


const logout =
  () => {

    const auth =
      getAuth();


    signOut(
      auth
    ).then(
      () => {

        localStorage.clear();

        router.push(
          "/"
        );

      }
    );

  };


/* ============================================================
   ICONS
============================================================ */

const getIcon =
  (name) => {

    return (
      `/dd-${name}-${theme.value}.png`
    );

  };

</script>


<style scoped>

/* ============================================================
   HEADER
============================================================ */

.feed-header {
  width: 100%;
  height: 80px;
  min-height: 80px;

  flex-shrink: 0;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  background:
    var(--background-color);

  z-index: 100;
}


/*
  ============================================================
  DESKTOP WORKING AREA

  Feed:
  filters      = 250px
  feed-content = 1188px

  250 + 1188 = 1438px

  Header живёт ровно внутри этой же ширины.
  ============================================================
*/

.feed-header-inner {
  width: 1438px;
  max-width: calc(100vw - 20px);

  height: 100%;

  margin: 0 auto;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  position: relative;
}


/* ============================================================
   LOGO AREA
============================================================ */

.header-logo-area {
  width: 250px;
  min-width: 250px;

  flex:
    0
    0
    250px;

  display: flex;
  align-items: center;
}


.header-logo {
  width: 40px;
  height: auto;

  flex-shrink: 0;

  display: block;

  object-fit: contain;

  cursor: pointer;

  transition:
    opacity .18s ease,
    transform .18s ease;
}


.header-logo:hover {
  opacity: .82;

  transform:
    translateY(-1px);
}


/* ============================================================
   NAVIGATION
============================================================ */

.nav-buttons {
  flex: 1;

  min-width: 0;

  display: flex;
  align-items: center;

  gap:
    clamp(
      14px,
      2vw,
      40px
    );

  overflow: hidden;

  white-space: nowrap;
}


.nav-buttons button {
  position: relative;

  flex-shrink: 0;

  display: inline-flex;
  align-items: center;

  gap: 8px;

  box-sizing: border-box;

  padding:
    8px
    0;

  color:
    var(--text-color);

  background:
    transparent;

  border:
    none;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    14px;

  line-height:
    1;

  letter-spacing:
    -0.35px;

  white-space:
    nowrap;

  cursor:
    pointer;
}


.nav-buttons button::after {
  content: "";

  position: absolute;

  left: 0;
  bottom: 2px;

  width: 0;
  height: 2px;

  background:
    var(--text-color);

  border-radius:
    999px;

  transition:
    width .22s ease;
}


.nav-buttons button:hover::after {
  width:
    100%;
}


.nav-buttons button.active {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    16px;
}


.nav-buttons button.active::after {
  width:
    0;
}


/* ============================================================
   TAB BADGE
============================================================ */

.tab-badge {
  flex-shrink: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    4px
    8px;

  color:
    #ffffff;

  background:
    linear-gradient(
      180deg,
      #ff9b2f 0%,
      #ff6a00 100%
    );

  border-radius:
    999px;

  box-shadow:
    0
    8px
    20px
    rgba(255, 122, 0, .22);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    9px;

  line-height:
    1;
}


/* ============================================================
   RIGHT SIDE
============================================================ */

.avatar-wrapper {
  position: relative;

  flex:
    0
    0
    auto;

  display: flex;
  align-items: center;

  gap: 12px;

  margin-left:
    auto;
}


/* ============================================================
   ACTION BUTTONS
============================================================ */

.action-buttons {
  height:
    46px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    stretch;

  overflow:
    hidden;

  background:
    var(--input-background);

  border:
    1px solid
    rgba(100, 115, 135, .045);

  border-radius:
    999px;
}


.icon-button {
  all:
    unset;

  position:
    relative;

  min-width:
    42px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  padding:
    0
    17px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    opacity .18s ease;
}


.icon-button:first-child {
  border-right:
    1px solid
    var(--separator_home);
}


.icon-button:hover {
  background:
    rgba(0, 0, 0, .045);
}


html[data-theme="dark"]
.icon-button:hover {
  background:
    rgba(255, 255, 255, .045);
}


.icon-button img {
  width:
    18px;

  height:
    18px;

  display:
    block;

  object-fit:
    contain;
}


/* ============================================================
   NOTIFICATION DOT
============================================================ */

.notification-dot {
  position:
    absolute;

  top:
    7px;

  right:
    10px;

  width:
    9px;

  height:
    9px;

  box-sizing:
    border-box;

  background:
    linear-gradient(
      180deg,
      #ffb648,
      #ff7a00
    );

  border:
    2px solid
    var(--background-color);

  border-radius:
    50%;

  box-shadow:
    0
    0
    0
    2px
    rgba(255, 122, 0, .15),
    0
    0
    10px
    rgba(255, 122, 0, .35);
}


/* ============================================================
   AVATAR
============================================================ */

.avatar-progress-wrapper {
  position:
    relative;

  width:
    52px;

  height:
    52px;

  flex:
    0
    0
    52px;

  cursor:
    pointer;

  transition:
    transform .22s ease;
}


.avatar-progress-wrapper:hover {
  transform:
    scale(1.06);
}


.avatar-progress-ring {
  position:
    absolute;

  inset: 0;

  width:
    52px;

  height:
    52px;

  transform:
    rotate(-90deg);

  z-index:
    3;

  pointer-events:
    none;
}


.progress-bg {
  fill:
    none;

  stroke:
    rgba(120, 125, 135, .14);

  stroke-width:
    3;
}


.progress-bar {
  fill:
    none;

  stroke:
    url(#avatarGradient);

  stroke-width:
    3.5;

  stroke-linecap:
    round;

  transition:
    stroke-dashoffset .6s ease;

  filter:
    drop-shadow(
      0
      0
      4px
      rgba(40, 135, 247, .48)
    );
}


.profile-avatar {
  position:
    absolute;

  top:
    3px;

  left:
    3px;

  width:
    46px;

  height:
    46px;

  display:
    block;

  box-sizing:
    border-box;

  object-fit:
    cover;

  background:
    var(--background-color);

  border:
    2px solid
    var(--background-color);

  border-radius:
    50%;

  box-shadow:
    0
    6px
    18px
    rgba(0, 0, 0, .12);

  z-index:
    2;

  transition:
    filter .2s ease;
}


.profile-avatar:hover {
  filter:
    brightness(1.05);
}


/* ============================================================
   AVATAR GLOW
============================================================ */

.avatar-progress-wrapper::after {
  content: "";

  position:
    absolute;

  inset:
    -4px;

  border-radius:
    50%;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, .25),
      transparent 65%
    );

  opacity:
    0;

  pointer-events:
    none;

  z-index:
    1;

  transition:
    opacity .25s ease;
}


.avatar-progress-wrapper:hover::after {
  opacity:
    1;
}


/* ============================================================
   AVATAR SKELETON
============================================================ */

.avatar-skeleton {
  width:
    46px;

  height:
    46px;

  flex:
    0
    0
    46px;

  border-radius:
    50%;

  background:
    #e0e0e0;
}


.shimmer {
  background:
    linear-gradient(
      90deg,
      #e0e0e0 25%,
      #f4f4f4 50%,
      #e0e0e0 75%
    );

  background-size:
    200% 100%;

  animation:
    shimmer
    1.2s
    infinite;
}


/* ============================================================
   TOOLTIP
============================================================ */

.profile-tooltip {
  position:
    absolute;

  right:
    calc(100% + 14px);

  top:
    50%;

  min-width:
    220px;

  box-sizing:
    border-box;

  padding:
    11px
    15px;

  color:
    var(--profile-tooltip-text);

  background:
    var(--profile-tooltip-bg);

  border:
    1px solid
    var(--profile-tooltip-border);

  border-radius:
    14px;

  box-shadow:
    var(--profile-tooltip-shadow);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  line-height:
    1.4;

  white-space:
    nowrap;

  opacity:
    0;

  pointer-events:
    none;

  transform:
    translateY(-50%)
    translateX(8px);

  transition:
    opacity .2s ease,
    transform .2s ease;

  z-index:
    20;

  backdrop-filter:
    blur(14px);
}


.profile-tooltip strong {
  color:
    #2887f7;

  font-family:
    "Eastman-Bold",
    sans-serif;
}


/* tooltip triangle */

.profile-tooltip::before {
  content: "";

  position:
    absolute;

  right:
    -6px;

  top:
    50%;

  width:
    12px;

  height:
    12px;

  background:
    var(--profile-tooltip-bg);

  border-top:
    1px solid
    var(--profile-tooltip-border);

  border-right:
    1px solid
    var(--profile-tooltip-border);

  transform:
    translateY(-50%)
    rotate(45deg);
}


.avatar-progress-wrapper:hover
.profile-tooltip {
  opacity:
    1;

  transform:
    translateY(-50%)
    translateX(0);
}


/* ============================================================
   SETTINGS DROPDOWN
============================================================ */

.settings-dropdown {
  position:
    absolute;

  top:
    calc(100% + 10px);

  right:
    0;

  width:
    260px;

  box-sizing:
    border-box;

  display:
    flex;

  flex-direction:
    column;

  gap:
    20px;

  padding:
    24px;

  color:
    var(--text);

  background:
    var(--settings-drop-down);

  border:
    1px solid
    rgba(120, 130, 145, .08);

  border-radius:
    18px;

  box-shadow:
    0
    20px
    40px
    rgba(0, 0, 0, .20);

  transform-origin:
    top right;

  animation:
    dropdownAppear
    .2s
    ease-out;

  z-index:
    9999;
}


/* ============================================================
   DROPDOWN SECTIONS
============================================================ */

.section {
  display:
    flex;

  flex-direction:
    column;

  gap:
    8px;
}


.section-title {
  margin-bottom:
    3px;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  letter-spacing:
    .6px;

  text-transform:
    uppercase;
}


.item,
.admin-item {
  position:
    relative;

  min-height:
    34px;

  display:
    flex;

  align-items:
    center;

  gap:
    12px;

  box-sizing:
    border-box;

  padding:
    7px
    8px;

  overflow:
    hidden;

  color:
    var(--text);

  border-radius:
    8px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    transform .18s ease,
    color .18s ease;
}


.item::before,
.admin-item::before {
  content: "";

  position:
    absolute;

  left:
    0;

  top:
    50%;

  width:
    2px;

  height:
    0;

  background:
    linear-gradient(
      180deg,
      #a075fc,
      #78a8ec
    );

  border-radius:
    0
    4px
    4px
    0;

  transform:
    translateY(-50%);

  transition:
    height .18s ease;
}


.item:hover,
.admin-item:hover {
  transform:
    translateX(3px);

  background:
    linear-gradient(
      90deg,
      rgba(33, 115, 229, .10),
      rgba(122, 66, 244, .035)
    );
}


.item:hover::before,
.admin-item:hover::before {
  height:
    60%;
}


.item .icon,
.admin-item .icon {
  width:
    17px;

  height:
    17px;

  flex:
    0
    0
    17px;

  object-fit:
    contain;

  transition:
    transform .18s ease;
}


.item:hover .icon,
.admin-item:hover .icon {
  transform:
    scale(1.07);
}


/* ============================================================
   BADGES
============================================================ */

.badge {
  margin-left:
    auto;

  padding:
    2px
    7px;

  color:
    #ffffff;

  background:
    #007aff;

  border-radius:
    999px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    10px;
}


.badge-skeleton {
  width:
    20px;

  height:
    16px;

  margin-left:
    auto;

  border-radius:
    999px;

  background:
    linear-gradient(
      90deg,
      #e0e0e0 25%,
      #f4f4f4 50%,
      #e0e0e0 75%
    );

  background-size:
    200% 100%;

  animation:
    shimmer
    1.2s
    infinite;
}


html[data-theme="dark"]
.badge-skeleton {
  background:
    linear-gradient(
      90deg,
      #3a3a3a 25%,
      #4a4a4a 50%,
      #3a3a3a 75%
    );

  background-size:
    200% 100%;
}


/* ============================================================
   DISABLED
============================================================ */

.item.disabled {
  opacity:
    .45;

  cursor:
    not-allowed;
}


.item.disabled:hover {
  transform:
    none;

  background:
    transparent;
}


.item.disabled:hover::before {
  height:
    0;
}


.item.disabled .icon {
  filter:
    grayscale(1);
}


.disabled-hint {
  margin-left:
    auto;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  white-space:
    nowrap;

  opacity:
    .7;
}


/* ============================================================
   HEADER SEPARATOR
============================================================ */

.header-separator {
  position:
    absolute;

  left:
    0;

  bottom:
    0;

  width:
    100%;

  height:
    1px;

  background:
    #e7e7e7;

  opacity:
    .9;
}


html[data-theme="dark"]
.header-separator {
  background:
    #1e1e1e;
}


/* ============================================================
   TOOLTIP THEME
============================================================ */

html[data-theme="dark"] {
  --profile-tooltip-bg:
    rgba(30, 32, 38, .97);

  --profile-tooltip-border:
    rgba(255, 255, 255, .10);

  --profile-tooltip-text:
    rgba(255, 255, 255, .82);

  --profile-tooltip-shadow:
    0
    18px
    40px
    rgba(0, 0, 0, .40);
}


html[data-theme="light"] {
  --profile-tooltip-bg:
    rgba(255, 255, 255, .97);

  --profile-tooltip-border:
    rgba(20, 35, 60, .10);

  --profile-tooltip-text:
    #465268;

  --profile-tooltip-shadow:
    0
    18px
    40px
    rgba(0, 0, 0, .10);
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 768px) {

  .feed-header {
    height:
      60px;

    min-height:
      60px;
  }


  .feed-header-inner {
    width:
      100%;

    max-width:
      none;

    padding:
      0
      12px;

    justify-content:
      space-between;
  }


  .header-logo-area {
    width:
      auto;

    min-width:
      0;

    flex:
      0
      0
      auto;
  }


  .header-logo {
    width:
      30px;
  }


  .nav-buttons {
    display:
      none;
  }


  .avatar-wrapper {
    margin-left:
      auto;

    gap:
      7px;
  }


  .action-buttons {
    height:
      36px;
  }


  .icon-button {
    min-width:
      36px;

    padding:
      0
      9px;
  }


  .icon-button img {
    width:
      16px;

    height:
      16px;
  }


  .notification-dot {
    top:
      5px;

    right:
      6px;

    width:
      8px;

    height:
      8px;
  }


  .avatar-progress-wrapper {
    width:
      40px;

    height:
      40px;

    flex-basis:
      40px;
  }


  .avatar-progress-ring {
    display:
      none;
  }


  .profile-avatar {
    position:
      static;

    width:
      40px;

    height:
      40px;

    border-width:
      1px;
  }


  .avatar-progress-wrapper::after,
  .profile-tooltip {
    display:
      none;
  }


  .settings-dropdown {
    position:
      fixed;

    top:
      68px;

    left:
      50%;

    right:
      auto;

    width:
      min(
        300px,
        calc(100vw - 24px)
      );

    padding:
      20px;

    transform:
      translateX(-50%);

    transform-origin:
      top center;

    animation:
      dropdownAppearMobile
      .2s
      ease-out;
  }

}


/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes shimmer {

  0% {
    background-position:
      200% 0;
  }

  100% {
    background-position:
      -200% 0;
  }

}


@keyframes dropdownAppear {

  from {
    opacity:
      0;

    transform:
      translateY(-7px)
      scale(.98);
  }

  to {
    opacity:
      1;

    transform:
      translateY(0)
      scale(1);
  }

}


@keyframes dropdownAppearMobile {

  from {
    opacity:
      0;

    transform:
      translateX(-50%)
      translateY(-7px)
      scale(.98);
  }

  to {
    opacity:
      1;

    transform:
      translateX(-50%)
      translateY(0)
      scale(1);
  }

}


@media (prefers-reduced-motion: reduce) {

  .header-logo,
  .avatar-progress-wrapper,
  .item,
  .admin-item {
    transition:
      none;
  }

}

.dialogs-dot {
  position: absolute;

  top: 12px;
  right: -12px;

  width: 6px;
  height: 6px;

  background:
    linear-gradient(
      180deg,
      #ffb648,
      #ff7a00
    );

  border:
    2px solid
    var(--background-color);

  border-radius: 50%;

  box-sizing: border-box;
  pointer-events: none;
}

</style>